import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  AnimationEvent,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  PointElement,
  ScriptableScaleContext,
  SubTitle,
  Title,
} from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { Options } from 'chartjs-plugin-datalabels/types/options';
import { isNumber } from 'lodash';
import { chartDatalabelFormatter, chartValueFormatter, formatUnit } from 'PDF/helpers/chartFormatters';
import { checkIsEmptyData } from 'PDF/helpers/pdfMapping';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { calculatePadding, splitLabel } from 'helpers/charts';

import { emptyChart, IChartWithBars } from '../types';

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, Legend, BarController, Title, SubTitle);

export interface IChartWithBarsProps {
  handleOnComplete: (uri: string) => void;
  info: IChartWithBars;
  aspectRatio: number;
  customDataLabels?: Options;
  withoutUnit?: boolean;
  withDataLabeInsideBar?: boolean;
}

ChartJS.defaults.font.family = 'Noto Sans';

const labelMainProperties = {
  font: {
    size: 26,
    weight: 300,
  },
  color: '#77787C',
};

const MAX_SUBTITLE_LENGTH = 125;

export const ChartWithBars = ({
  info: { labels, data, datasetsLabels, colors, source, unit = '' },
  aspectRatio,
  handleOnComplete,
  customDataLabels,
  withoutUnit,
  withDataLabeInsideBar = false,
}: IChartWithBarsProps) => {
  const unitData = withoutUnit ? '' : unit;
  const { t } = useTranslation()
  const { isExamplePage } = useExamplePreview();

  // const max = getAbsMaxValue(data);
  const allValues: string[] = [
    ...data.flatMap((dataset) => dataset.map((value) => (isNumber(value) ? Math.ceil(value).toString() : value))),
  ];

  const chartData = {
    labels,
    datasets: data.map((item, index) => ({
      type: 'bar' as const,
      label: datasetsLabels[index],
      backgroundColor: colors[index],
      data: item,
      borderWidth: 0,
      barPercentage: 1,
      categoryPercentage: data.length > 2 ? 0.7 : 0.5,
      ...(customDataLabels && {
        datalabels: {
          ...customDataLabels,
          color: colors[index],
        },
      }),
    })),
  };

  const unitPlugin = [
    {
      id: 'unitPlugin',
      afterDraw(chart: ChartJS) {
        const ctx = chart.ctx;
        const leftX = chart.chartArea?.left;

        ctx.fillStyle = labelMainProperties.color;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.font = '300 24px Noto Sans';

        ctx.fillText(formatUnit(unit), leftX - 18, 20);
      },
    },
  ];

  const options: ChartOptions = {
    aspectRatio,
    devicePixelRatio: 1,
    layout: {
      padding: {
        left: calculatePadding(allValues, unit),
      },
    },
    plugins: {
      title: {
        display: Boolean(unit),
        text: '',
        align: 'start',
        padding: { bottom: 40 },
        font: {
          size: 24,
          weight: 300,
        },
        color: labelMainProperties.color,
      },
      subtitle: {
        display: Boolean(source),
        text: `${isExamplePage ? 'Source' : t('source')}: ${source?.substring(0, MAX_SUBTITLE_LENGTH)}`,
        position: 'bottom',
        align: 'start',
        padding: { top: 35 },
        ...labelMainProperties,
      },
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 26,
          boxHeight: 26,
          padding: 30,
          ...labelMainProperties,
          font: {
            ...labelMainProperties.font,
            weight: 300,
          },
        },
      },
      datalabels: {
        display: true,
        anchor: (context: Context) => {
          const value = context.dataset.data[context.dataIndex] ?? 0;
          return withDataLabeInsideBar ? 'start' : +value >= 0 ? 'end' : 'start';
        },
        align: (context: Context) => {
          const value = context.dataset.data[context.dataIndex] ?? 0;
          return withDataLabeInsideBar || +value >= 0 ? 'top' : 'bottom';
        },
        font: {
          size: 26,
          weight: 400,
        },
        color: (context: Context) => {
          const backgroundColor = context.dataset.backgroundColor ?? 'black';
          return withDataLabeInsideBar ? 'white' : (backgroundColor as string);
        },
        formatter: withDataLabeInsideBar ? chartValueFormatter : chartDatalabelFormatter,
      },
    },
    scales: {
      y: {
        display: true,
        ticks: {
          // stepSize: +(max / MAX_NUMBER_OF_TICKS).toFixed(1),
          color: '#77787C',
          font: {
            size: 26,
            weight: 300,
          },
          padding: 10,
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? '#77787C' : 'transparent'),
          tickBorderDash: [3, 7],
        },
        border: { display: false, dash: [3, 7] },
      },
      x: {
        display: true,
        ticks: {
          ...labelMainProperties,
          maxRotation: 0,
          callback: function (_: unknown, index: number) {
            return splitLabel(labels[index].toString());
          },
        },
        grid: {
          color: 'transparent',
        },
        border: { display: true, color: 'transparent' },
      },
    },
    animation: {
      duration: 0,
      onComplete(event: AnimationEvent) {
        handleOnComplete(checkIsEmptyData(data) ? emptyChart : event.chart.toBase64Image());
      },
    },
  };

  return (
    <Chart type="bar" data={chartData} options={options} plugins={[ChartDataLabels, ...(unitData ? unitPlugin : [])]} />
  );
};
